/**
 * 2026 병오년 심층 명리학 내러티브 엔진
 * 4주(年月日時) 전체 분석 · 조후 · 천간합 · 원진귀문 완전 반영
 */
import type { SajuResult } from './sajuCalculations';

// ── 2026 세운 ───────────────────────────────────────────────────
const YS = 2; // 병(丙)
const YB = 6; // 오(午)

// ── 지지 한자/한글 ──────────────────────────────────────────────
const BKR = ['자','축','인','묘','진','사','오','미','신','유','술','해'];
const BCN = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const SKR = ['갑','을','병','정','무','기','경','신','임','계'];
const SCN = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];

// ── 십신 계산 ───────────────────────────────────────────────────
const EL = [0,0,1,1,2,2,3,3,4,4]; // stem→element idx
const GEN=[1,2,3,4,0], CTRL=[2,3,4,0,1], GENBY=[4,0,1,2,3], CTRLBY=[3,4,0,1,2];
function sipseong(dayIdx: number, targetIdx: number): string {
  const d=EL[dayIdx],t=EL[targetIdx],dy=dayIdx%2,ty=targetIdx%2;
  if(t===d) return dy===ty?'비견':'겁재';
  if(t===GEN[d]) return dy===ty?'식신':'상관';
  if(t===CTRL[d]) return dy===ty?'편재':'정재';
  if(t===CTRLBY[d]) return dy===ty?'편관':'정관';
  if(t===GENBY[d]) return dy===ty?'편인':'정인';
  return '비견';
}

// ── 지지 관계 완전판 ────────────────────────────────────────────
type BRel = '충'|'삼합'|'방합'|'육합'|'형'|'자형'|'파'|'해'|'원진'|'귀문'|'없음';
function branchRels(a: number, b: number): BRel[] {
  const rels: BRel[] = [];
  // 충
  if((a+6)%12===b||(b+6)%12===a) rels.push('충');
  // 육합
  const yh:number[][] = [[0,1],[2,11],[3,10],[4,9],[5,8],[6,7]];
  if(yh.some(p=>(p[0]===a&&p[1]===b)||(p[1]===a&&p[0]===b))) rels.push('육합');
  // 삼합
  const sh:number[][] = [[2,6,10],[11,3,7],[5,9,1],[8,0,4]];
  if(sh.some(g=>g.includes(a)&&g.includes(b)&&a!==b)) rels.push('삼합');
  // 방합
  const fh:number[][] = [[2,3,4],[5,6,7],[8,9,10],[11,0,1]];
  if(fh.some(g=>g.includes(a)&&g.includes(b)&&a!==b)) rels.push('방합');
  // 형
  const hy:number[][] = [[2,5],[5,8],[8,2],[1,10],[10,7],[7,1],[0,3],[3,0]];
  if(hy.some(p=>p[0]===a&&p[1]===b)) rels.push('형');
  if(a===b&&[4,6,9,11].includes(a)) rels.push('자형');
  // 파
  const pa:number[][] = [[0,9],[9,0],[6,3],[3,6],[4,1],[1,4],[7,10],[10,7],[2,11],[11,2],[5,8],[8,5]];
  if(pa.some(p=>p[0]===a&&p[1]===b)) rels.push('파');
  // 해
  const hae:number[][] = [[0,7],[7,0],[1,6],[6,1],[2,5],[5,2],[3,4],[4,3],[8,11],[11,8],[9,10],[10,9]];
  if(hae.some(p=>p[0]===a&&p[1]===b)) rels.push('해');
  // 원진
  const wj:number[][] = [[0,7],[7,0],[1,6],[6,1],[2,9],[9,2],[3,8],[8,3],[4,11],[11,4],[5,10],[10,5]];
  if(wj.some(p=>p[0]===a&&p[1]===b)) rels.push('원진');
  // 귀문관살
  const gm:number[][] = [[9,0],[0,9],[6,1],[1,6],[7,2],[2,7],[8,3],[3,8],[11,4],[4,11],[10,5],[5,10]];
  if(gm.some(p=>p[0]===a&&p[1]===b)) rels.push('귀문');
  return rels.length ? rels : ['없음'];
}

function relName(a: number, b: number, rels: BRel[]): string {
  const an=BCN[a], bn=BCN[b], ak=BKR[a], bk=BKR[b];
  return rels.filter(r=>r!=='없음').map(r=>`${an}${bn} ${r}(${ak}${bk}${
    r==='충'?'冲':r==='삼합'?'三合':r==='방합'?'方合':r==='육합'?'六合':r==='형'?'刑':r==='자형'?'自刑':r==='파'?'破':r==='해'?'害':r==='원진'?'怨嗔':'鬼門'
  })`).join(' · ');
}

// 천간합 감지
function stemComb(a: number, b: number): string|null {
  if((b-a+10)%10===5||(a-b+10)%10===5) {
    const p = Math.min(a,b);
    const map:Record<number,string> = {0:'갑기합(甲己合)→토',1:'을경합(乙庚合)→금',2:'병신합(丙辛合)→수',3:'정임합(丁壬合)→목',4:'무계합(戊癸合)→화'};
    return map[p%5]||null;
  }
  return null;
}

// ── 일간 물상 ───────────────────────────────────────────────────
const METAPHOR: Record<number,{ko:string;metaphor:string;nature:string}> = {
  0:{ko:'갑목(甲木)',metaphor:'높은 산의 소나무',nature:'위로 뻗어오르는 강한 생명력'},
  1:{ko:'을목(乙木)',metaphor:'유연한 풀과 덩굴',nature:'환경에 맞춰 살아남는 끈질긴 생명력'},
  2:{ko:'병화(丙火)',metaphor:'하늘의 태양',nature:'모든 것을 밝게 드러내는 양기의 정수'},
  3:{ko:'정화(丁火)',metaphor:'어둠을 밝히는 촛불',nature:'은은하지만 가치 있는 빛'},
  4:{ko:'무토(戊土)',metaphor:'거대한 산과 대지',nature:'묵직하게 만물을 받쳐주는 든든한 기반'},
  5:{ko:'기토(己土)',metaphor:'만물을 키우는 논밭',nature:'세심하게 기르고 결실 맺게 하는 따뜻한 땅'},
  6:{ko:'경금(庚金)',metaphor:'날카로운 도끼와 원석',nature:'강인하게 단련되어 가치를 발하는 금속'},
  7:{ko:'신금(辛金)',metaphor:'정밀하게 다듬어진 보석',nature:'예리하고 빛나는 가치'},
  8:{ko:'임수(壬水)',metaphor:'넓고 깊은 바다',nature:'모든 것을 포용하는 지혜'},
  9:{ko:'계수(癸水)',metaphor:'생명을 키우는 빗물',nature:'조용하지만 깊이 있는 지혜'},
};

// ── 십신 × 물상 → 총론 메타포 ──────────────────────────────────
const SIP_METAPHOR: Record<string,string> = {
  '비견': '또 하나의 태양이 같은 하늘에 뜨는 형국이라, 경쟁과 협력이 교차합니다.',
  '겁재': '거대한 태양(丙)이 촛불의 영역을 침범하는 해, 나의 것이 위협받습니다.',
  '식신': '내 에너지가 창조적으로 발산되어 재능이 만개하는 해입니다.',
  '상관': '강한 햇살이 풀을 태울 수도, 더욱 무성하게 만들 수도 있는 양날의 해입니다.',
  '편재': '태양빛이 바다 위에 부서지며 가치를 드러내는 재물의 해입니다.',
  '정재': '태양이 들판을 비추어 성실한 노력이 결실을 맺는 해입니다.',
  '편관': '뜨거운 불이 원석을 용광로에 넣어 단련시키는 해, 압박이 곧 도약의 발판입니다.',
  '정관': '태양이 보석을 찬란하게 비추어 공식적인 인정과 지위가 따라오는 해입니다.',
  '편인': '태양이 산을 달구며 내면의 직관과 지혜를 깨우는 해입니다.',
  '정인': '태양이 논밭을 따뜻이 비추어 귀인과 어른의 지원이 이어지는 해입니다.',
};

// ── 조후(調候) 분석 ─────────────────────────────────────────────
function chohuText(monthBranch: number, dayStemIdx: number): string {
  const season = [3,3,0,0,0,1,1,1,2,2,2,3][monthBranch];
  // 0=봄, 1=여름, 2=가을, 3=겨울
  const dayEl = EL[dayStemIdx];
  if(season===3) return `${BKR[monthBranch]}월(겨울)에 태어난 사주에게 병오(丙午)의 화기(火氣)는 단순한 세운이 아닙니다. 얼어붙은 세상에 온기를 불어넣는 조후(調候)의 역할도 합니다. 위협이자 구원, 두 얼굴을 가진 해입니다.`;
  if(season===1) return `${BKR[monthBranch]}월(여름)에 태어난 사주에 또 다시 강렬한 병오(丙午)의 화기가 더해집니다. 과열(過熱)을 조심해야 합니다. 열기를 식혀주는 금(金)·수(水) 기운이 이 해의 핵심 처방입니다.`;
  if(season===2) return `${BKR[monthBranch]}월(가을)에 태어난 사주에 병오(丙午)의 화기가 들어옵니다. 가을의 금기(金氣)를 화극금(火剋金)으로 누르는 구조라, 기존에 안정됐던 것들이 도전을 받습니다.`;
  return `${BKR[monthBranch]}월(봄)에 태어난 사주에 병오(丙午)의 화기가 들어오면 봄의 목기(木氣)가 화기를 생(生)해주며 성장 에너지가 배가됩니다.`;
}

// ── 궁위(宮位) 의미 ─────────────────────────────────────────────
const GW: Record<'년'|'월'|'일'|'시',string> = {
  년:'조상·초년·사회적 뿌리', 월:'부모·형제·직업·사회생활', 일:'배우자·나 자신·현재 환경', 시:'자녀·노년·부하·미래',
};

// ── 궁위별 관계 내러티브 ─────────────────────────────────────────
function pillarRelText(pos: '년'|'월'|'일'|'시', branchKor: string, branchCn: string, relList: BRel[], relStr: string): string {
  const gw = GW[pos];
  if(relList.includes('없음')||relList.length===0) return '';
  const hasChung = relList.includes('충');
  const hasHap = relList.includes('삼합')||relList.includes('방합')||relList.includes('육합');
  const hasPa = relList.includes('파');
  const hasHae = relList.includes('해');
  const hasWj = relList.includes('원진');
  const hasGm = relList.includes('귀문');

  if(hasChung) return `${pos}주(${branchCn})와 세운지 午가 **${relStr}**을 이룹니다. ${gw}을 담당하는 ${pos}주가 세운의 충격을 정면으로 받는 구조입니다. 충(冲)은 기존의 안정을 흔들어 변화를 강제합니다. 두렵겠지만 이 충격이 새로운 국면을 만들어냅니다.`;
  if(hasHap) return `${pos}주(${branchCn})와 세운지 午가 **${relStr}**을 이룹니다. ${gw}에 해당하는 에너지가 올해 화기와 합쳐져 더욱 강해집니다. 이 에너지를 잘 활용하면 해당 분야에서 큰 도약이 가능합니다.`;
  if(hasPa) return `${pos}주(${branchCn})와 세운지 午가 **${relStr}**을 이룹니다. 파(破)는 겉으로는 멀쩡해 보이지만 서서히 균열이 생기는 기운입니다. ${gw}에서 예상치 못한 변수가 발생하기 쉬우니 사전 점검이 필요합니다.`;
  if(hasWj && hasGm) return `${pos}주(${branchCn})와 세운지 午가 **${relStr}**을 이룹니다. 원진(怨嗔)과 귀문관살(鬼門關殺)이 겹쳐 ${gw}에 정신적 피로와 내면의 갈등이 쌓이기 쉽습니다. 불면증, 신경과민, 가까운 사람과의 이유 없는 마찰이 이 복합살의 전형적 증상입니다.`;
  if(hasHae||hasWj) return `${pos}주(${branchCn})와 세운지 午가 **${relStr}**을 이룹니다. ${gw}에서 겉으로 드러나지 않는 방해와 갈등이 생기기 쉽습니다. 주변의 시기, 오해, 배신 등에 주의가 필요합니다.`;
  return `${pos}주(${branchCn})와 세운지 午가 ${relStr}를 이룹니다.`;
}

// ── 월별 내러티브 생성 ───────────────────────────────────────────
interface MonthNarrative {
  month: number; solarMonth: string; ganji: string; score: number;
  title: string; detail: string; tags: string[];
}

const MONTHS_2026 = [
  {m:1,sm:'2월',ganji:'경인(庚寅)',si:6,bi:2},
  {m:2,sm:'3월',ganji:'신묘(辛卯)',si:7,bi:3},
  {m:3,sm:'4월',ganji:'임진(壬辰)',si:8,bi:4},
  {m:4,sm:'5월',ganji:'계사(癸巳)',si:9,bi:5},
  {m:5,sm:'6월',ganji:'갑오(甲午)',si:0,bi:6},
  {m:6,sm:'7월',ganji:'을미(乙未)',si:1,bi:7},
  {m:7,sm:'8월',ganji:'병신(丙申)',si:2,bi:8},
  {m:8,sm:'9월',ganji:'정유(丁酉)',si:3,bi:9},
  {m:9,sm:'10월',ganji:'무술(戊戌)',si:4,bi:10},
  {m:10,sm:'11월',ganji:'기해(己亥)',si:5,bi:11},
  {m:11,sm:'12월',ganji:'경자(庚子)',si:6,bi:0},
  {m:12,sm:'1월(27)',ganji:'신축(辛丑)',si:7,bi:1},
];

function genMonthNarrative(mData: typeof MONTHS_2026[0], result: SajuResult): MonthNarrative {
  const {m,sm,ganji,si,bi} = mData;
  const dayS = result.dayPillar.stemIdx, dayB = result.dayPillar.branchIdx;
  const monS = result.monthPillar.stemIdx, monB = result.monthPillar.branchIdx;
  const yrB = result.yearPillar.branchIdx;
  const hrB = result.hourPillar?.branchIdx;

  const sip = sipseong(dayS, si);
  const tComb = stemComb(dayS, si);

  // 월지 vs 각 원국 지지 관계 수집
  const interactions: Array<{pos:string; rels:BRel[]; rStr:string; biB:number}> = [];
  const checkList: Array<[string, number]> = [
    ['세운지 午', YB], ['년지', yrB], ['월지', monB], ['일지', dayB],
  ];
  if(hrB !== undefined) checkList.push(['시지', hrB]);

  for(const [posLabel, origB] of checkList) {
    if(bi === origB && posLabel !== '세운지 午') continue; // same branch = 자형만 체크
    const rels = branchRels(bi, origB);
    if(!rels.includes('없음')) {
      const rStr = relName(bi, origB, rels);
      interactions.push({pos: posLabel, rels, rStr, biB: origB});
    }
  }

  // 점수 계산
  let score = 62;
  for(const {rels} of interactions) {
    if(rels.includes('삼합')||rels.includes('육합')) score += 10;
    if(rels.includes('방합')) score += 6;
    if(rels.includes('충')) score -= 12;
    if(rels.includes('형')||rels.includes('자형')) score -= 7;
    if(rels.includes('파')) score -= 5;
    if(rels.includes('해')||rels.includes('원진')) score -= 5;
    if(rels.includes('귀문')) score -= 4;
  }
  const goodSip = ['식신','정재','정인','편재','정관'];
  const badSip = ['편관','겁재','상관'];
  if(goodSip.includes(sip)) score += 6;
  if(badSip.includes(sip)) score -= 6;
  if(tComb) score += 8;
  score = Math.max(28, Math.min(96, score));

  // 태그 생성
  const tags: string[] = [sip];
  for(const {rStr} of interactions) tags.push(rStr);
  if(tComb) tags.push(tComb);

  // 제목 + 상세 텍스트
  const criticalRels = interactions.filter(i=>i.rels.includes('충')||i.rels.includes('형'));
  const goodRels = interactions.filter(i=>i.rels.includes('삼합')||i.rels.includes('육합'));

  let title = '', detail = '';
  const mBKR = BKR[bi], mBCN = BCN[bi];

  // 천간합 달 — 특별 처리
  if(tComb) {
    const combName = tComb.split('→')[0];
    const daySkr = SKR[dayS], mSkr = SKR[si];
    title = `${ganji}월 · ${combName} — 귀인이 찾아오는 달`;
    detail = `${ganji}월의 천간 ${SCN[si]}(${mSkr})과 일간 ${SCN[dayS]}(${daySkr})이 ${combName}을 이룹니다. 천간합이 성립하는 달에는 나와 인연이 깊은 귀인이 나타나 힘이 되어주고, 내 능력을 알아주는 시기가 됩니다. 직장·사업·인간관계에서 긍정적인 전환점이 만들어집니다.`;
    if(interactions.length) detail += ` 다만 지지에서는 ${interactions.map(i=>i.rStr).join(', ')}이 함께 작용하니 세부 사항을 꼼꼼히 챙겨야 합니다.`;
    return {month:m,solarMonth:sm,ganji,score:Math.min(96,score+5),title,detail,tags};
  }

  // 충 있는 달 — 가장 상세하게
  if(criticalRels.length) {
    const chungList = criticalRels.map(i=>`${GW[i.pos.replace('지','') as '년'|'월'|'일'|'시']||i.pos} ${i.rStr}`).join(', ');
    title = `${ganji}월 · ${criticalRels.map(i=>i.rStr).join(' ')} — 격변의 한 달`;
    detail = `${ganji}월은 올해 가장 긴장해야 할 달 중 하나입니다. 이 달 들어오는 ${mBCN}(${mBKR})이 ${chungList}를 일으킵니다. `;
    if(criticalRels.some(i=>i.pos.includes('월지'))) detail += `직업·사회관계(월주)에 직접적인 충격이 가해지는 구조입니다. 부서 이동, 이직, 예상치 못한 업무 변화가 발생할 수 있습니다. `;
    if(criticalRels.some(i=>i.pos.includes('일지'))) detail += `배우자궁·나 자신(일주)에도 충격이 오는 시기라 건강과 인간관계 변동에 주의하세요. `;
    detail += `충(冲)은 나쁜 것이 아니라 변화를 강제하는 에너지입니다. 두려워하지 말고 파도에 올라타는 결단이 필요합니다.`;
    if(goodRels.length) detail += ` 한편 ${goodRels.map(i=>i.rStr).join(', ')}도 동시에 작용하여 변화를 뒷받침하는 호조도 있습니다.`;
    return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
  }

  // 합 있는 달 — 좋은 달
  if(goodRels.length) {
    const hapStr = goodRels.map(i=>i.rStr).join(', ');
    title = `${ganji}월 · ${hapStr} — 에너지가 응집되는 달`;
    detail = `${ganji}월의 ${mBCN}(${mBKR})이 ${hapStr}을 이루어 에너지가 한 곳으로 응집됩니다. 합이 이루어지는 달에는 새로운 인연·계약·합작이 성사되기 좋습니다. 지금까지 준비해온 것이 구체적 결과로 나타나기 시작합니다.`;
    if(goodRels.some(i=>i.rels.includes('삼합'))) detail += ` 특히 삼합(三合)의 경우 화기(火氣)의 흐름이 강화되며 올해 전체 에너지의 결절점이 됩니다.`;
    return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
  }

  // 파/해/원진/귀문 있는 달
  const softNeg = interactions.filter(i=>i.rels.some(r=>['파','해','원진','귀문'].includes(r)));
  if(softNeg.length) {
    const relStr = softNeg.map(i=>i.rStr).join(', ');
    title = `${ganji}월 · ${relStr} — 보이지 않는 균열을 조심하라`;
    detail = `${ganji}월에는 ${relStr}이 작용합니다. 충처럼 극적이진 않지만 서서히 균열이 생기는 기운입니다. 겉으로는 평온해 보여도 내면적 갈등, 예상치 못한 지출, 주변의 오해가 생기기 쉽습니다. 섣불리 새로운 일을 벌이기보다 현재를 단단히 지키는 달입니다.`;
    return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
  }

  // 일반 달
  const goodOrBad = score >= 65 ? '순조로운' : score >= 50 ? '무난한' : '다소 조용한';
  title = `${ganji}월 · ${sip} — ${goodOrBad} 달`;
  detail = `${ganji}월 천간은 일간에게 ${sip}으로 작용합니다. ${
    goodSip.includes(sip) ? '나의 능력이 자연스럽게 드러나고 현재 하는 일에서 성과가 쌓이는 달입니다. 특별한 이벤트보다 꾸준함이 빛을 발합니다.'
    : badSip.includes(sip) ? '에너지가 과하게 소모되거나 경쟁이 심해질 수 있는 달입니다. 무리한 도전보다 내실을 다지는 전략이 유리합니다.'
    : '큰 변동 없이 흘러가는 달입니다. 장기 계획을 구체화하거나 준비를 갖추는 데 적합합니다.'
  }`;
  return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
}

// ── 메인 분석 함수 ───────────────────────────────────────────────
export interface DeepNarrative {
  reportTitle: string;
  pillarsStr: string; // "甲戌 丙子 丁酉 辛丑"
  dayPillarLabel: string;
  overallScore: number;
  seunSipseong: string;
  // 총론
  overviewTitle: string;
  overviewP1: string;
  overviewP2: string;
  overviewP3: string;
  strategy: string;
  // 4주 분석
  pillarAnalysis: Array<{pos:'년'|'월'|'일'|'시'; label:string; pillarStr:string; rels:BRel[]; relStr:string; text:string}>;
  // 분야별
  careerText: string;
  moneyText: string;
  healthText: string;
  loveText: string;
  // 월별
  months: MonthNarrative[];
}

export function generateDeepNarrative(result: SajuResult): DeepNarrative {
  const dayS = result.dayPillar.stemIdx;
  const dayB = result.dayPillar.branchIdx;
  const monS = result.monthPillar.stemIdx;
  const monB = result.monthPillar.branchIdx;
  const yrS = result.yearPillar.stemIdx;
  const yrB = result.yearPillar.branchIdx;
  const hrS = result.hourPillar?.stemIdx;
  const hrB = result.hourPillar?.branchIdx;

  const meta = METAPHOR[dayS];
  const sip = sipseong(dayS, YS);
  const sipM = SIP_METAPHOR[sip];

  // 4주 지지 vs 세운지 오(午)
  const pillarData: Array<{pos:'년'|'월'|'일'|'시'; branchIdx:number; pillarStr:string}> = [
    {pos:'년', branchIdx:yrB, pillarStr:`${SCN[yrS]}${BCN[yrB]}`},
    {pos:'월', branchIdx:monB, pillarStr:`${SCN[monS]}${BCN[monB]}`},
    {pos:'일', branchIdx:dayB, pillarStr:`${SCN[dayS]}${BCN[dayB]}`},
    ...(hrB!==undefined&&hrS!==undefined ? [{pos:'시' as const, branchIdx:hrB, pillarStr:`${SCN[hrS]}${BCN[hrB]}`}] : []),
  ];

  const pillarAnalysis = pillarData.map(({pos, branchIdx, pillarStr}) => {
    const rels = branchRels(branchIdx, YB);
    const rStr = relName(branchIdx, YB, rels);
    const text = rels.includes('없음') ? '' : pillarRelText(pos, BKR[branchIdx], BCN[branchIdx], rels, rStr);
    return {pos, label:`${pos}주(${pillarStr})`, pillarStr, rels, relStr:rStr, text};
  }).filter(p=>!p.rels.includes('없음'));

  // 종합 점수
  let score = 60;
  const sipScores: Record<string,number> = {'식신':82,'정재':80,'정인':78,'정관':75,'편재':76,'편인':70,'비견':65,'상관':62,'겁재':56,'편관':54};
  score = sipScores[sip] || 65;
  for(const p of pillarAnalysis) {
    if(p.rels.includes('삼합')||p.rels.includes('육합')) score += 7;
    if(p.rels.includes('방합')) score += 4;
    if(p.rels.includes('충')) score -= (p.pos==='월'?10:7);
    if(p.rels.includes('파')||p.rels.includes('해')) score -= 4;
    if(p.rels.includes('원진')&&p.rels.includes('귀문')) score -= 8;
  }
  score = Math.max(30, Math.min(95, score));

  // 총론 생성
  const dayCN = `${result.dayPillar.stem}${result.dayPillar.branch}`;
  const mainChungs = pillarAnalysis.filter(p=>p.rels.includes('충'));
  const mainHaps = pillarAnalysis.filter(p=>p.rels.includes('삼합')||p.rels.includes('육합')||p.rels.includes('방합'));

  const p1 = `${dayCN} 일주, ${meta.metaphor}(${meta.ko})를 타고난 당신에게 2026년 병오년(丙午年)은 결코 평범한 해가 아닙니다. 세운 천간 병화(丙火)는 당신의 일간에게 **${sip}** 운이 됩니다. ${sipM}`;
  const p2 = chohuText(monB, dayS);
  const chungDesc = mainChungs.length ? `특히 주목해야 할 것은 ${mainChungs.map(p=>`${p.pos}주 ${p.relStr}`).join(', ')}입니다. ${mainChungs.map(p=>`${GW[p.pos]}을 담당하는 ${p.pos}주가 올해 세운의 충격을 정면으로 받습니다.`).join(' ')}` : '';
  const hapDesc = mainHaps.length ? ` 반면 ${mainHaps.map(p=>`${p.pos}주 ${p.relStr}`).join(', ')}의 호조도 함께 작용합니다.` : '';
  const p3 = (chungDesc + hapDesc) || `4주의 지지가 세운지 午와 큰 충돌 없이 흘러가 상대적으로 안정된 한 해입니다.`;

  const strategy = sip === '겁재'||sip === '편관' ? `올해는 방어전이 아닌 공격전입니다. 거센 경쟁의 에너지를 이기려 들지 말고, 그 흐름 위에 올라타는 전략이 핵심입니다. 내 성과는 반드시 증거를 남기고, 주도적으로 어필하세요.`
    : sip === '식신'||sip === '편재'||sip === '정재' ? `올해는 재능을 숨기지 마세요. 드러낼수록, 표현할수록 기회가 찾아오는 해입니다. 들어오는 재물은 반드시 일부를 안전 자산으로 묶어두는 원칙이 필요합니다.`
    : sip === '정관'||sip === '정인' ? `올해는 정직하게 원칙대로 행동하는 것이 가장 빠른 길입니다. 귀인의 도움이 따르니 주변의 제안에 열린 마음을 유지하세요.`
    : `올해는 큰 변화보다 내실을 다지는 것이 핵심입니다. 지금 가진 것을 지키면서 기회를 기다리는 인내가 필요합니다.`;

  // 분야별 텍스트 (월주/일주/시주 관계 기반으로 구체화)
  const monthPillarRel = pillarAnalysis.find(p=>p.pos==='월');
  const dayPillarRel = pillarAnalysis.find(p=>p.pos==='일');
  const hourPillarRel = pillarAnalysis.find(p=>p.pos==='시');

  const careerText = monthPillarRel
    ? `직업·사회활동을 담당하는 월주(${monthPillarRel.pillarStr})와 세운지 午 사이에 ${monthPillarRel.relStr}이 작용합니다. ${monthPillarRel.rels.includes('충') ? `자오충(子午沖)처럼 강한 충이 月主를 건드리면 직장 내 부서 이동, 이직, 독립 창업 등 피할 수 없는 커리어 지각변동이 일어납니다. 두려워하지 말고, 이 에너지가 도약의 발판임을 기억하세요. 내 성과는 반드시 기록으로 남기고 적극 어필해야 합니다.` : monthPillarRel.rels.includes('삼합')||monthPillarRel.rels.includes('육합') ? `합(合)은 직업적 에너지가 더욱 강해지는 신호입니다. 새로운 기회·계약·파트너십이 자연스럽게 찾아오는 해입니다.` : `직업적 환경에서 ${monthPillarRel.relStr}의 기운이 작용합니다. 보이지 않는 변화와 마찰에 대비하세요.`}`
    : `${sip} 세운에서 직업운은 ${['식신','정관','정인'].includes(sip) ? '재능을 인정받고 성과가 공식화되는 흐름입니다.' : ['겁재','편관'].includes(sip) ? '경쟁이 치열해지고 압박이 강해집니다. 하지만 신강한 사주라면 이 압박이 오히려 도약의 계기가 됩니다.' : '꾸준히 노력하면 결과가 따라오는 흐름입니다.'}`;

  const moneyText = dayPillarRel
    ? `배우자궁이자 재물의 실질적 창고인 일지(${result.dayPillar.branch})에 ${dayPillarRel.relStr}이 작용합니다. ${dayPillarRel.rels.includes('파') ? `파(破)는 창고에 균열이 생기는 기운입니다. 돈이 안 들어오는 게 아니라, 들어온 돈이 빠르게 빠져나가는 패턴이 나타납니다. 동업 제안, 지인 금전 부탁, 충동 투자가 재물을 갉아먹는 주범이 될 수 있습니다. 수입의 일부를 반드시 인출하기 어려운 형태(부동산, 적금)로 묶어두세요.` : dayPillarRel.rels.includes('충') ? `충이 일지를 건드리면 재물 변동이 크게 발생합니다. 큰 수입이 생길 수도, 예상치 못한 지출이 터질 수도 있습니다. 재정 관리를 더욱 철저히 해야 하는 해입니다.` : `일지에서 ${dayPillarRel.relStr}이 작용합니다. 재물 흐름에 주의하며 안정적인 자산 관리가 필요합니다.`}`
    : `${sip} 세운에서 재물운은 ${['편재','정재'].includes(sip) ? '좋은 흐름입니다. 단 들어오는 만큼 나가는 구조이므로 수입의 일부는 반드시 저축하세요.' : ['겁재','상관'].includes(sip) ? '주의가 필요합니다. 경쟁자나 예기치 못한 지출로 재물이 새기 쉬운 해입니다.' : '꾸준히 관리하면 안정적으로 유지됩니다.'}`;

  const healthText = hourPillarRel
    ? `노년·가정·내면을 담당하는 시주(${hourPillarRel.pillarStr})와 세운지 午 사이에 ${hourPillarRel.relStr}이 작용합니다. ${hourPillarRel.rels.includes('원진')&&hourPillarRel.rels.includes('귀문') ? `원진(怨嗔)과 귀문관살(鬼門關殺)이 겹치는 구조는 정신건강에 적신호입니다. 머릿속은 생각으로 꽉 차고, 불면증과 신경과민이 생기기 쉽습니다. 심혈관·혈압·안과 건강을 특히 체크하고, '아무것도 하지 않는' 의도적 휴식과 수영·반신욕 등 열기를 식히는 활동이 처방입니다.` : `시주에서의 변화가 가정 환경과 내면의 안정에 영향을 줍니다. 가까운 사람에게 예민해지거나 번아웃이 올 수 있으니 의도적 휴식을 챙기세요.`}`
    : `올해 화기가 강한 만큼 심혈관·혈압·눈 건강에 주의가 필요합니다. 체력이 넘친다고 쉬지 않으면 갑작스러운 번아웃으로 이어집니다.`;

  const loveText = (() => {
    const isDohwa = [0,3,6,9].includes(dayB) || [0,3,6,9].includes(YB);
    const base = isDohwa ? `일지(${result.dayPillar.branchKor}) 또는 세운지 午는 자(子)·오(午)·묘(卯)·유(酉) 중 하나로, 명리학에서 도화살(桃花殺)에 해당합니다. 도화가 활성화되는 해에는 매력이 폭발하고 대인관계가 넓어지지만, 이성 관계에서 구설수나 감정 소모가 따를 수 있습니다. 인맥을 넓히는 것보다 진짜 내 사람을 챙기는 것이 우선입니다.` : '';
    if(dayPillarRel?.rels.includes('파')) return base + ` 일지에 파(破)가 작용하여 배우자궁이 흔들리는 해입니다. 연인이나 배우자와 잦은 오해와 다툼이 생기기 쉬우니, 한 박자 늦추는 대화법이 필요합니다.`;
    if(dayPillarRel?.rels.includes('육합')||dayPillarRel?.rels.includes('삼합')) return base + ` 일지에 합(合)이 작용하여 새로운 인연이나 결합이 이루어지기 좋은 해입니다. 결혼·동거·진지한 관계 전환을 고려하고 있다면 올해가 적기입니다.`;
    return base || `대인관계는 ${sip}의 기운에 따라 ${['겁재','상관'].includes(sip) ? '활발하지만 갈등도 잦은 편입니다. 말을 아끼는 습관이 관계를 지켜줍니다.' : '전반적으로 원만하게 흘러갑니다.'}`;
  })();

  // 월별
  const months = MONTHS_2026.map(mData => genMonthNarrative(mData, result));

  const pillarsStr = [
    result.yearPillar.stem + result.yearPillar.branch,
    result.monthPillar.stem + result.monthPillar.branch,
    result.dayPillar.stem + result.dayPillar.branch,
    result.hourPillar ? result.hourPillar.stem + result.hourPillar.branch : '',
  ].filter(Boolean).join(' ');

  return {
    reportTitle: `2026년 병오년(丙午年) 심층 운명 분석 리포트`,
    pillarsStr,
    dayPillarLabel: `${result.dayPillar.stem}${result.dayPillar.branch} 일주 (${meta.ko})`,
    overallScore: score,
    seunSipseong: sip,
    overviewTitle: `"${dayCN} 일주, 병오년(丙午年)과 만나다"`,
    overviewP1: p1,
    overviewP2: p2,
    overviewP3: p3,
    strategy,
    pillarAnalysis,
    careerText,
    moneyText,
    healthText,
    loveText,
    months,
  };
}